import { FileDownload } from "@mui/icons-material";
import { DataType, SortDirection } from "ka-table";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import DropdownMenu from "../../../App/Components/DropdownMenu/DropdownMenu";
import { ExportAttachmentsModal } from "../../../App/Components/Export/ExportAttachmentsModal";
import CrmTable, { DEFAULT_SELECTION_COLUMN } from "../../../App/Components/Table/CrmTable";
import { useCampaignFormDataQuery } from "../../../App/Queries/CampaignQueries";
import { useSelectedClientCustomFields } from "../../../App/Queries/ClientQueries";
import {
  exportGroupMembers,
  useExportGroupAttachments,
  useFilteredGroupMembersPagingQuery,
} from "../../../App/Queries/GroupQueries";
import { useAuthToken } from "../../../App/State/Hooks/authHooks";
import { useNotification } from "../../../App/State/Hooks/notificationHooks";
import { useGroupCandidatesPaginationStore } from "../../../App/State/Stores/GroupCandidatesPaginationStore";
import { ICampaignFormState } from "../../../App/Typings/Campaigns";
import { Field, ICandidate, ICandidateTableListing } from "../../../App/Typings/Candidate";
import { Rule } from "../../../App/Typings/DynamicGroupRules";
import { ICandidateTableProps, ITablePagination } from "../../../App/Typings/SortableTable";
import { KaColumn } from "../../../App/Typings/TableColumn";
import { clearOldLocalStorage } from "../../../App/Utilities/browserStorage";
import { useGroupCandidateSelectionContext } from "../../State/GroupCandidateSelectionContext";
import { useGroupProfileContext } from "../../State/GroupProfileContext";
import { CandidateActions } from "../CandidateActions/CandidateActions";

const paginationOptions: ITablePagination = {
  rowsPerPage: [10, 25, 50, 100],
  enabled: true,
};

const DEFAULT_COLUMNS: KaColumn[] = [
  {
    title: "Date Added",
    key: "createdDate",
    dataType: DataType.Date,
    sortDirection: SortDirection.Descend,
    isSortable: true,
    type: "default",
  },
  {
    title: "Name",
    key: "fullName",
    dataType: DataType.String,
    type: "default",
  },
  {
    title: "Company",
    key: "company",
    dataType: DataType.String,
    visible: false,
    type: "default",
  },
  {
    title: "Title",
    key: "title",
    dataType: DataType.String,
    visible: false,
    type: "default",
  },
  {
    title: "Phone Numbers",
    key: "phoneNumbers",
    dataType: DataType.Object,
    type: "default",
  },
  {
    title: "Email Addresses",
    key: "emailAddresses",
    dataType: DataType.Object,
    type: "default",
  },
  {
    title: "Last Updated",
    key: "lastUpdatedDate",
    dataType: DataType.Date,
    type: "default",
  },
  {
    title: "Tags",
    key: "tags",
    dataType: DataType.Object,
    type: "default",
  },
  {
    title: "Locations",
    key: "locations",
    dataType: DataType.Object,
    type: "default",
  },
  {
    title: "Notes",
    key: "notes",
    dataType: DataType.Object,
    visible: false,
    type: "default",
  },
  {
    title: "Attachments",
    key: "attachments",
    dataType: DataType.Object,
    isEditable: false,
    isSortable: false,
    isFilterable: false,
    type: "default",
  },
];

const DEFAULT_ACTIONS_COLUMN: KaColumn = {
  title: "Actions",
  key: "actions",
  dataType: DataType.Object,
  isEditable: false,
  isSortable: false,
  isFilterable: false,
  width: 100,
  type: "action",
};

const DEFAULT_COLUMN_KEYS = new Set(DEFAULT_COLUMNS.map((col) => col.key));
DEFAULT_COLUMN_KEYS.add(DEFAULT_ACTIONS_COLUMN.key);
DEFAULT_COLUMN_KEYS.add(DEFAULT_SELECTION_COLUMN.key);

interface IGroupTableProps extends Partial<ICandidateTableProps> {
  isTableVisible: boolean;
  onEditTagsClick: (candidate: ICandidate) => void;
  handleSelectCandidateToRemove: (candidateId: string) => void;
}

const customDataWithUniqueKeys = (data: ICandidate[]) => data.map((item) => ({ ...item, rowKey: item.id }));

const getExportButtonLabel = ({
  selectAll,
  selectedCount,
  excludedCount,
  totalCount,
}: {
  selectAll: boolean;
  selectedCount: number;
  excludedCount: number;
  totalCount: number;
}): string => {
  if (selectAll) {
    if (excludedCount === 0) {
      return "Export All";
    }
    const exportCount = totalCount - excludedCount;
    return `Export All ${exportCount.toLocaleString()}`;
  }
  if (selectedCount > 0) {
    return `Export ${selectedCount.toLocaleString()} Candidate${selectedCount === 1 ? "" : "s"}`;
  }
  return "Export";
};

export function GroupTable({
  isTableVisible,
  handleSelectCandidateToRemove,
  onEditTagsClick,
}: IGroupTableProps) {
  const token = useAuthToken();
  const navigate = useNavigate();
  const { data: customFields = [] } = useSelectedClientCustomFields();
  const { group } = useGroupProfileContext();
  const {
    getters: { isCandidateSelected, selectedCandidateIdList, selectAll, excludedCandidateIds, selectedCount },
    setters: { setActionCandidate, setTotalAvailableCount },
    handlers: { handleSelectedAll, handleSelectRow },
  } = useGroupCandidateSelectionContext();

  const { filteredCandidateList, isLoading, totalItems } = useFilteredGroupMembersPagingQuery(
    group.id,
    isTableVisible,
  );
  const { data: campaign, isLoading: isLoadingCampaignData } = useCampaignFormDataQuery<ICampaignFormState>(
    (group.isDynamic ? group.dynamicGroupCampaignId : group.campaignId) ?? "",
  );
  const { page, pageSize, sortBy, sortDirection, handleChangePage, handleChangePageSize, handleRequestSort } =
    useGroupCandidatesPaginationStore(group.id);

  // Update total available count when total items changes
  useEffect(() => {
    if (totalItems !== undefined) {
      setTotalAvailableCount(totalItems);
    }
  }, [totalItems, setTotalAvailableCount]);

  const tableData: ICandidateTableListing[] = filteredCandidateList.map((candidate) => {
    var data = {
      ...candidate,
      actions: (
        <CandidateActions
          onEditTagsClick={onEditTagsClick}
          candidate={candidate}
          handleSelectCandidateToRemove={handleSelectCandidateToRemove}
          setSelectedCandidate={setActionCandidate}
          canRemoveCandidates={!group.isDynamic}
        />
      ),
    } as any;

    customFields.forEach((c) => {
      const accessKey = DEFAULT_COLUMN_KEYS.has(c.key) ? `__${c.key}` : c.key;
      data[accessKey] = printValue(data.fields.filter((f: Field) => f.key === c.key)[0]?.value);
    });

    return data;
  });

  let customData = [...tableData];

  const [exportAttachmentsModalOpen, setExportAttachmentsModalOpen] = useState<boolean>(false);
  const { displaySuccess, displayError } = useNotification();
  const { mutateAsync: exportAttachments } = useExportGroupAttachments(group.id);

  const handleExportAttachments = async () => {
    await exportAttachments(
      {
        selectAll,
        candidateIds: selectedCandidateIdList,
        excludedIds: excludedCandidateIds,
      },
      {
        onSuccess: (successResponse) => {
          if (successResponse.success) {
            displaySuccess("Exporting attachments, you will receive an email shortly.");
          } else {
            displayError(successResponse.message ?? "Error exporting attachments, please try again.");
          }
        },
        onError: () => {
          displayError("Error exporting attachments, please contact support.");
        },
      },
    );
  };

  const allColumns = useMemo(() => {
    const dynamicGroupFields = getRuleFields(group?.rules ?? []);
    let cols = [...DEFAULT_COLUMNS];

    const visibleFields = group.isDynamic
      ? dynamicGroupFields
      : Object.values(campaign?.formFields ?? [])
          .filter((f) => f.enabled && !f.archived)
          .map((f) => f.key);

    if (customFields.length) {
      cols = cols.concat(
        customFields.map((c) => {
          const key = DEFAULT_COLUMN_KEYS.has(c.key) ? `__${c.key}` : c.key;
          return {
            key,
            dataType: DataType.String,
            title: c.label,
            visible: group.isDynamic || !!group.campaignId ? visibleFields.includes(c.key) : false,
            isSortable: false,
            type: "custom",
          };
        }),
      );
    }

    cols.push(DEFAULT_ACTIONS_COLUMN);

    cols = cols.map((col) => {
      if (col.key === sortBy) {
        return {
          ...col,
          sortDirection: sortDirection === "desc" ? SortDirection.Descend : SortDirection.Ascend,
          isSortable: true,
        };
      } else {
        return {
          ...col,
          sortDirection: undefined,
        };
      }
    });

    return cols;
  }, [customFields, campaign, group, sortBy, sortDirection]);

  return group &&
    group.id &&
    (group.campaignId || group.dynamicGroupCampaignId ? campaign && !isLoadingCampaignData : true) ? (
    <>
      <CrmTable
        name={`group-candidates-${group.id}`}
        columns={allColumns}
        loading={isLoading}
        data={customDataWithUniqueKeys(customData)}
        rowId="rowKey"
        rowSelection={{
          onRowSelect: (row: ICandidate, selected: boolean) => handleSelectRow(row, selected),
          isRowSelected: (row) => isCandidateSelected(row.id),
          onAllSelected: handleSelectedAll,
        }}
        pagination={{
          ...paginationOptions,
          totalCount: totalItems,
          currentPage: page,
          rows: pageSize,
          handleChangePage,
          handleChangeRowsPerPage: handleChangePageSize,
        }}
        order={sortDirection}
        orderBy={sortBy}
        onSort={(prop) => handleRequestSort(prop)}
        onRowClick={(row) => navigate(`/candidates/${row.id}`)}
        rowTestId={(row) => `candidate-${row.id}`}
        ExportButton={(cols) => {
          const customFieldKeys = cols
            .filter((c) => c.visible && !DEFAULT_COLUMN_KEYS.has(c.key))
            .map((c) => c.key);
          const exportLabel = getExportButtonLabel({
            selectAll,
            selectedCount,
            excludedCount: excludedCandidateIds.length,
            totalCount: totalItems ?? 0,
          });
          return (
            <DropdownMenu
              buttonLabel={exportLabel}
              tooltipText="Export candidate data or attachments"
              buttonIcon={<FileDownload color="primary" />}
              menuItems={[
                {
                  label: "Candidate Data",
                  title: "Export candidate data using your current filters",
                  onClick: async () =>
                    await exportGroupMembers(token, group.clientId, group.id, {
                      selectAll,
                      candidateIds: selectedCandidateIdList,
                      excludedIds: excludedCandidateIds,
                      customFields: customFieldKeys,
                    }),
                },
                {
                  label: "Candidate Attachments",
                  title: "Export candidate attachments using your current filters",
                  onClick: () => setExportAttachmentsModalOpen(true),
                },
              ]}
            />
          );
        }}
      />
      <ExportAttachmentsModal
        isOpen={exportAttachmentsModalOpen}
        handleCloseModal={() => setExportAttachmentsModalOpen(false)}
        onExportAttachmentsClick={async () => {
          await handleExportAttachments();
          setExportAttachmentsModalOpen(false);
        }}
      />
    </>
  ) : (
    <></>
  );
}

const getRuleFields = (rules: Rule[]) => {
  let fields: string[] = [];
  const excludedFields = [
    "createdDate",
    "emailAddress",
    "phoneNumber",
    "firstName",
    "lastName",
    "campaignId",
    "tags",
    "geolocation",
  ];

  for (let i = 0; i < rules.length; i++) {
    if (rules[i].rules && rules[i].rules.length > 0) {
      fields = fields.concat(getRuleFields(rules[i].rules));
    }
    if (rules[i].filter.field !== "") {
      let fieldName = rules[i].filter.field.substring(rules[i].filter.field.lastIndexOf(".") + 1);
      if (!fields.includes(fieldName) && !excludedFields.includes(fieldName)) {
        fields.push(fieldName);
      }
    }
  }
  return fields;
};

const printValue = (value: any) => {
  if (value) {
    return Array.isArray(value) ? value.join(", ") : value;
  }

  return "";
};

clearOldLocalStorage("group-candidates");
